import type { ParallelAINodeData } from "./workflow.types";

/**
 * The Parallel AI price book, and the two multipliers Nexus composes on top of
 * it. ONE copy, read by every surface that quotes or charges a Parallel AI run.
 *
 * 🔴 THIS FILE HOLDS THE NUMBERS AND NOTHING ELSE. The arithmetic deliberately
 * stays with each consumer: the backend charges in ten-thousandths of USD with
 * its rounding pinned by spec, the frontend quotes in whole cents. A shared
 * `calculate()` would have to pick one rounding convention and would silently
 * change what an organization is billed. Sharing the DATA is what stops the two
 * surfaces disagreeing; sharing the arithmetic is what would make them agree on
 * the wrong number.
 *
 * ## Why it lives in `@nexus/types` rather than beside the executor
 *
 * It was two copies, and they diverged: the backend applied
 * `PROVIDER_COST_MULTIPLIER` and `PLATFORM_MARKUP_PERCENTAGE`, the frontend
 * estimator applied neither, and a customer read a pre-run quote at exactly a
 * quarter of what the run would cost on the standard tiers. A price a user reads
 * and a price a user is charged are the same fact and now have one home.
 *
 * Everything here is plain data and type-level derivation — no Prisma, no
 * runtime dependency — so the frontend may import it directly.
 */

/**
 * The tiers Parallel AI charges PER 1000 REQUESTS, taken STRAIGHT from the
 * node's own type rather than restated here.
 *
 * That derivation is the point. A tier list copied into this file can be added
 * to in `workflow-node-ai.types.ts` and never copied across — which is exactly how four
 * tiers came to be offered in the UI and billed at the `base` rate. Keying the
 * price tables off the node type instead means adding a tier there is a COMPILE
 * ERROR here, in the `Record` literals below, until it is priced.
 *
 * Two of the node's three selectors are priced this way: `processor` (Task and
 * Task Group) and `enrichmentProcessor` (Find All enrichment, which contributes
 * `core2x`). Enrichment belongs here because Parallel bills it at the Task
 * processor rate, once per match.
 *
 * 🔴 `findAllGenerator` is DELIBERATELY ABSENT, and its absence is the fix in
 * this file. Find All is not priced per 1000 runs at all — it is a fixed cost
 * per run plus a cost per match, which is `FIND_ALL_VENDOR_USD` below. The two
 * key spaces share the words `base`, `core` and `pro`, and folding them into one
 * union is exactly why a `pro` Find All run was charged at the `pro` TASK rate.
 * A collision of names is not a shared price table.
 */
export type ParallelAIRequestTier = NonNullable<
  ParallelAINodeData["processor"] | ParallelAINodeData["enrichmentProcessor"]
>;

/**
 * The pipeline a Parallel Search request runs, derived from the node's own type.
 *
 * `parallel-search-mode.ts` on the backend derives the identical alias for the
 * executor's use. Both descend from `ParallelAINodeData["searchMode"]`, so a
 * mode added there is a compile error in both places and the two cannot drift.
 */
export type ParallelSearchMode = NonNullable<ParallelAINodeData["searchMode"]>;

/**
 * The billing key one Parallel SEARCH request is charged under.
 *
 * 🔴 SEARCH IS PRICED BY MODE, NOT BY PROCESSOR, and these keys exist because
 * it was billed by processor anyway. `executeSearch` charged
 * `nodeData.processor` — a TASK tier that the Search API does not even accept
 * (its own `processor` field is marked "DEPRECATED: use `mode` instead"). So a
 * search on a node left at the default `base` was billed the $10/1000 Task rate
 * for a $5/1000 search, and the same search on a node set to `ultra` was billed
 * $300/1000: a 60x spread over one unchanging vendor price, decided by a
 * selector that did not reach the request.
 *
 * Prefixed rather than reusing the bare mode names on purpose. `basic` and
 * `advanced` do not collide with a Task tier today, but `FindAllGenerator`
 * already taught this price book what a name collision costs — a `pro` Find All
 * run billed at the `pro` TASK rate — so the two key spaces stay textually
 * disjoint.
 */
export type ParallelAISearchBillingKey = `search_${ParallelSearchMode}`;

/** The billing key for one search in `mode`. The only way to build one. */
export const searchBillingKey = (mode: ParallelSearchMode): ParallelAISearchBillingKey =>
  `search_${mode}`;

/**
 * Every billing key Parallel AI per-request usage can be charged under: every
 * per-request tier, plus `extract` for the Extract API, which is priced per URL
 * rather than per run and is passed as a literal by the node, plus one key per
 * Search mode.
 */
export type ParallelAIBillingKey = ParallelAIRequestTier | "extract" | ParallelAISearchBillingKey;

/**
 * The Search modes at runtime, for building the billing keys below.
 *
 * `satisfies` closes one direction — a value here that is not a mode is an
 * error. `ASSERT_EVERY_SEARCH_MODE_IS_LISTED` closes the other.
 */
export const PARALLEL_SEARCH_MODES_FOR_PRICING = [
  "turbo",
  "basic",
  "advanced"
] as const satisfies readonly ParallelSearchMode[];

type UnlistedSearchMode = Exclude<
  ParallelSearchMode,
  (typeof PARALLEL_SEARCH_MODES_FOR_PRICING)[number]
>;

const ASSERT_EVERY_SEARCH_MODE_IS_LISTED: UnlistedSearchMode extends never
  ? true
  : ["Parallel search mode missing from PARALLEL_SEARCH_MODES_FOR_PRICING:", UnlistedSearchMode] =
  true;
void ASSERT_EVERY_SEARCH_MODE_IS_LISTED;

/**
 * The same key space at runtime, for iteration and for the type guard.
 *
 * `satisfies` makes the tuple exhaustive in ONE direction — a key here that is
 * not a billing key is an error. `ASSERT_EVERY_TIER_IS_LISTED` below closes the
 * other direction, so the tuple can neither gain a stray key nor miss a real one.
 */
export const PARALLEL_AI_BILLING_KEYS = [
  "lite",
  "base",
  "core",
  "core2x",
  "pro",
  "ultra",
  "ultra2x",
  "ultra4x",
  "ultra8x",
  "extract",
  // Derived from the mode list rather than spelled out, so a mode added to the
  // node type cannot be offered in the UI without a price: the `Record`
  // literals below stop compiling until it has one.
  ...PARALLEL_SEARCH_MODES_FOR_PRICING.map(searchBillingKey)
] as const satisfies readonly ParallelAIBillingKey[];

/**
 * Compile-time proof that the tuple above lists EVERY billing key.
 *
 * A tier added to the node type and missing from the tuple resolves this alias
 * to the missing key rather than `never`, and the assignment below stops
 * compiling with that key named in the error text.
 */
type UnlistedBillingKey = Exclude<ParallelAIBillingKey, (typeof PARALLEL_AI_BILLING_KEYS)[number]>;

const ASSERT_EVERY_TIER_IS_LISTED: UnlistedBillingKey extends never
  ? true
  : ["Parallel AI tier missing from PARALLEL_AI_BILLING_KEYS:", UnlistedBillingKey] = true;
void ASSERT_EVERY_TIER_IS_LISTED;

const BILLING_KEY_SET: ReadonlySet<string> = new Set<string>(PARALLEL_AI_BILLING_KEYS);

export function isParallelAIBillingKey(value: string): value is ParallelAIBillingKey {
  return BILLING_KEY_SET.has(value);
}

/**
 * Parallel AI's own list price, in USD per 1000 requests.
 *
 * Source: https://docs.parallel.ai/resources/pricing (read 2026-08-14) and
 * https://parallel.ai/pricing (re-verified 2026-08-31, tier for tier, against
 * both that page and https://docs.parallel.ai/getting-started/pricing, which
 * agree with each other). No number here is inferred or interpolated.
 *
 *   lite 5 · base 10 · core 25 · core2x 50 · pro 100
 *   ultra 300 · ultra2x 600 · ultra4x 1200 · ultra8x 2400
 *   extract 1 (per 1000 URLs)
 *
 * The `-fast` processor variants are not separate keys: Parallel's docs state
 * that "Fast processors have the same pricing as their standard counterparts",
 * so they are the same rate at a different latency.
 *
 * Search is priced per 1000 SEARCHES on its own two-price ladder, published at
 * https://docs.parallel.ai/search/modes (read 2026-08-15) and restated in
 * Parallel's Search Turbo announcement:
 *
 *   search_turbo 1 (~200ms)
 *   search_basic 5 (~1s) · search_advanced 5 (~3s)
 */
export const VENDOR_USD_PER_1000: Record<ParallelAIBillingKey, number> = {
  lite: 5,
  base: 10,
  core: 25,
  core2x: 50,
  pro: 100,
  ultra: 300,
  ultra2x: 600,
  ultra4x: 1200,
  ultra8x: 2400,
  extract: 1,
  search_turbo: 1,
  search_basic: 5,
  search_advanced: 5
};

/**
 * 🔴 CONVENTION, NOT A PUBLISHED RATE.
 *
 * Nexus records a provider cost ABOVE Parallel's list price, on a curve that
 * predates this price book: the cheap tiers carry 2x and the deep-research tiers
 * carry 1.3x. Parallel publishes no such split — it is a Nexus convention, and
 * it is stated per family so nobody has to infer which family a new tier joined.
 * `ultra2x`, `ultra4x` and `ultra8x` are the ultra family (the same processor at
 * 2x, 4x and 8x compute) and take 1.3; `core2x` is the core family and takes 2.
 */
export const DEEP_RESEARCH_MULTIPLIER = 1.3;
export const STANDARD_MULTIPLIER = 2;

export const PROVIDER_COST_MULTIPLIER: Record<ParallelAIBillingKey, number> = {
  lite: STANDARD_MULTIPLIER,
  base: STANDARD_MULTIPLIER,
  core: STANDARD_MULTIPLIER,
  core2x: STANDARD_MULTIPLIER,
  pro: DEEP_RESEARCH_MULTIPLIER,
  ultra: DEEP_RESEARCH_MULTIPLIER,
  ultra2x: DEEP_RESEARCH_MULTIPLIER,
  ultra4x: DEEP_RESEARCH_MULTIPLIER,
  ultra8x: DEEP_RESEARCH_MULTIPLIER,
  extract: STANDARD_MULTIPLIER,
  // Every Search mode takes the standard curve. `search_advanced` shares a word
  // with the deep-research Task tiers and nothing else: it is a ~3s web search
  // at $5/1000, not an `ultra` research run, so it is not on their curve.
  search_turbo: STANDARD_MULTIPLIER,
  search_basic: STANDARD_MULTIPLIER,
  search_advanced: STANDARD_MULTIPLIER
};

/**
 * The platform's own markup, as a percentage of the provider cost.
 *
 * 🔴 100 MEANS THE ORGANIZATION PAYS TWICE THE PROVIDER COST, and the doubling
 * lives in the CALLERS rather than in any single multiply. Each consumer
 * computes `providerCost + platformCost`, and at 100% `platformCost` equals
 * `providerCost` — so the composed total is `2 x providerCost`. Reading this
 * constant as a `x1` multiply on its own is correct about the multiply and wrong
 * about the total; reading it as `x2` is correct about the total and wrong about
 * the multiply. It is the sum that is billed.
 */
export const PLATFORM_MARKUP_PERCENTAGE = 100;

/**
 * The Find All generators — the node's third selector, and the ONLY one priced
 * per run rather than per 1000 requests.
 */
export type FindAllGenerator = NonNullable<ParallelAINodeData["findAllGenerator"]>;

/**
 * Parallel AI's own list price for Find All, in USD.
 *
 * Source: https://docs.parallel.ai/resources/pricing (read 2026-08-14) and
 * https://parallel.ai/pricing (re-verified 2026-08-31). Find All is charged as a
 * FIXED amount per run plus an amount per MATCH — never per 1000 runs:
 *
 *   preview  $0.10 + $0.00 per match
 *   base     $0.25 + $0.03 per match
 *   core     $2.00 + $0.15 per match
 *   pro     $10.00 + $1.00 per match
 *
 * The ENRICHMENT leg is not in this table on purpose. The same page states that
 * "each enrichment adds its own per-match cost based on the Task API processor
 * you choose", so enrichment is priced by `VENDOR_USD_PER_1000` above, once per
 * match — which is why `enrichmentProcessor` is a per-request tier and
 * `findAllGenerator` is not.
 */
export const FIND_ALL_VENDOR_USD: Record<
  FindAllGenerator,
  { readonly fixedPerRun: number; readonly perMatch: number }
> = {
  preview: { fixedPerRun: 0.1, perMatch: 0 },
  base: { fixedPerRun: 0.25, perMatch: 0.03 },
  core: { fixedPerRun: 2, perMatch: 0.15 },
  pro: { fixedPerRun: 10, perMatch: 1 }
};

/**
 * 🔴 CONVENTION, NOT A PUBLISHED RATE. Overrule it here, in one place.
 *
 * Find All has no provider-cost convention of its own. These values carry the
 * Task curve across BY NAME: `preview`, `base` and `core` take the standard 2x,
 * and `pro` takes the deep-research 1.3x, on the reading that a generator and a
 * Task processor sharing a name share a research depth. That is a reading. It is
 * stated as a named table rather than inlined so that changing it is one edit
 * and so that nobody has to infer which family a generator joined.
 */
export const FIND_ALL_PROVIDER_COST_MULTIPLIER: Record<FindAllGenerator, number> = {
  preview: STANDARD_MULTIPLIER,
  base: STANDARD_MULTIPLIER,
  core: STANDARD_MULTIPLIER,
  pro: DEEP_RESEARCH_MULTIPLIER
};

/**
 * The Find All key space at runtime, for iteration and for the type guard.
 * Same two-directional exhaustiveness proof as `PARALLEL_AI_BILLING_KEYS`.
 */
export const FIND_ALL_GENERATORS = [
  "preview",
  "base",
  "core",
  "pro"
] as const satisfies readonly FindAllGenerator[];

type UnlistedFindAllGenerator = Exclude<FindAllGenerator, (typeof FIND_ALL_GENERATORS)[number]>;

const ASSERT_EVERY_FIND_ALL_GENERATOR_IS_LISTED: UnlistedFindAllGenerator extends never
  ? true
  : ["Find All generator missing from FIND_ALL_GENERATORS:", UnlistedFindAllGenerator] = true;
void ASSERT_EVERY_FIND_ALL_GENERATOR_IS_LISTED;

const FIND_ALL_GENERATOR_SET: ReadonlySet<string> = new Set<string>(FIND_ALL_GENERATORS);

export function isFindAllGenerator(value: string): value is FindAllGenerator {
  return FIND_ALL_GENERATOR_SET.has(value);
}

/**
 * The key an unrecognised processor is billed and quoted under.
 *
 * Node data is a persisted JSON column that can hold any string, so neither
 * surface can close this by types alone. Both fall back to the same key, so a
 * quote and a charge for the same unrecognised tier still agree with each other.
 */
export const FALLBACK_BILLING_KEY: ParallelAIBillingKey = "base";

/** The generator an unrecognised Find All generator is billed and quoted under. */
export const FALLBACK_FIND_ALL_GENERATOR: FindAllGenerator = "base";

/** Provider cost in USD per 1000 requests: list price times the family curve. */
export function providerUsdPer1000(key: ParallelAIBillingKey): number {
  return VENDOR_USD_PER_1000[key] * PROVIDER_COST_MULTIPLIER[key];
}
